import { useState, useEffect, lazy, Suspense, useMemo } from 'react';
import { trackPageView } from '../lib/analytics';
import useSWR, { mutate } from 'swr';
import { Layout } from '../components/Layout';
import { SEO } from '../components/SEO';
import { Header } from '../components/Header';
import { NewsCard } from '../components/NewsCard';
import { NewsCardSkeleton, FeaturedRowSkeleton, CategoriesSkeleton, HomeGridSkeleton, CategoryGridSkeleton } from '../components/Loaders';
import { bloggerService } from '../services/blogger';
import { Link } from 'react-router-dom';
import { BreakingNewsTicker } from '../components/BreakingNewsTicker';
import { PullToRefresh } from '../components/PullToRefresh';
import { extractFirstImage, cn, stripHtml, formatBDDate } from '../lib/utils';
import { db } from '../lib/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useGlobals } from '../lib/useGlobals';
import { LayoutSection } from '../admin/components/builder/types';
import { DynamicSection } from '../components/DynamicSection';
import { WebStoriesRow } from '../components/WebStoriesRow';
import { StickyAdminBreakingNews } from '../components/StickyAdminBreakingNews';

const YoutubeGallery = lazy(() => import('../components/YoutubeGallery'));
const PhotoGallery = lazy(() => import('../components/PhotoGallery').then(module => ({ default: module.PhotoGallery })));
const AdBanner = lazy(() => import('../components/AdBanner').then(module => ({ default: module.AdBanner })));
const WidgetPostList = lazy(() => import('../components/WidgetPostList').then(module => ({ default: module.WidgetPostList })));

const CompactPost = ({ post, imageVariant = "standard" }: { post: any, imageVariant?: "standard" | "small" | "square" }) => {
  const imageUrl = extractFirstImage(post.content, 300, 168);
  
  let imageClass = "w-[85px] h-[58px] xl:w-[95px] xl:h-[65px]";
  let gapClass = "gap-3";
  if (imageVariant === "small") {
    imageClass = "w-[70px] h-[50px] xl:w-[80px] xl:h-[55px]";
    gapClass = "gap-1.5";
  } else if (imageVariant === "square") {
    imageClass = "w-[70px] h-[70px] xl:w-[80px] xl:h-[80px]";
  }

  return (
    <Link to={post.url ? new URL(post.url).pathname : `/post/${post.id}`} className="flex flex-col group">
      <div className={`flex ${gapClass} justify-between items-start mb-1.5`}>
         <h3 className="font-bold text-[15px] xl:text-[16px] leading-[1.35] text-[#111111] dark:text-gray-100 group-hover:text-[#D32F2F] transition-colors font-bengali flex-1 lg:mt-[-1px]">
           {post.title}
         </h3>
         {imageUrl && (
           <div className={`shrink-0 overflow-hidden relative bg-gray-100 dark:bg-gray-800 ml-1 ${imageClass}`}>
             <img src={imageUrl} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" />
           </div>
         )}
      </div>
      <div className="text-[#555555] dark:text-gray-400 text-[13px] line-clamp-2 font-bengali leading-relaxed mb-2">
         {stripHtml(post.content).substring(0, 120)}...
      </div>
      <div className="text-[11.5px] text-[#888888] dark:text-gray-500 font-bengali">
         {formatBDDate(post.published, true)}
      </div>
    </Link>
  );
};

const AdSenseWidget = () => (
  <div className="bg-[#F9F9F9] dark:bg-gray-800/80 rounded border border-[#E5E5E5] dark:border-gray-800 h-full flex items-center justify-center min-h-[200px] overflow-hidden p-2">
    <AdBanner />
  </div>
);

function findMatchingLabel(target: string, available: string[]): string | null {
  if (!target || !available || available.length === 0) return null;
  
  const clean = (str: string) => {
    return str
      .toLowerCase()
      .trim()
      // Normalize common Bengali character variations
      .replace(/[\u09BC]/g, '') // remove nukta
      .replace(/ো/g, 'ও')
      .replace(/ে/g, 'এ')
      .replace(/ী/g, 'ি') // normalize long/short i
      .replace(/ূ/g, 'u') // normalize long/short u
      .replace(/\s+/g, '');
  };

  const targetClean = clean(target);

  // 1. Try exact normalized match
  for (const label of available) {
    if (clean(label) === targetClean) {
      return label;
    }
  }

  // 2. Try partial match (target is substring of label)
  for (const label of available) {
    if (clean(label).includes(targetClean)) {
      return label;
    }
  }

  // 3. Try partial match (label is substring of target)
  for (const label of available) {
    if (targetClean.includes(clean(label))) {
      return label;
    }
  }

  return null;
}

export function Home() {
  const [activeCategory, setActiveCategory] = useState<string>('সব');
  const [showAllLatest, setShowAllLatest] = useState(false);

  const fetchKey = activeCategory === 'সব' ? 'all_posts' : `category_posts_${activeCategory}`;
  
  const fetcher = () => {
    if (activeCategory === 'সব') {
      return bloggerService.getPosts(undefined, 20);
    } else {
      return bloggerService.getPostsByLabel(activeCategory, 20);
    }
  };

  const { data: postsData, isLoading: postsLoading } = useSWR(fetchKey, fetcher, { keepPreviousData: true });

  const { data: categoriesData } = useSWR('categories', () => bloggerService.getCategories());
  const categories = categoriesData || [];

  const resolvedLabels = useMemo(() => {
    const available = categoriesData || [];
    const used = new Set<string>();
    const mapping: Record<string, string> = {};

    const getMappingFor = (key: string, original: string) => {
      const match = findMatchingLabel(original, available);
      if (match) {
        used.add(match);
        mapping[key] = match;
      } else {
        mapping[key] = original; // default to original
      }
    };

    // Main layout sections
    getMappingFor('national', 'জাতীয়');
    getMappingFor('bd', 'বাংলাদেশ');
    getMappingFor('world', 'বিশ্ব');
    getMappingFor('politics', 'রাজনীতি');
    getMappingFor('economy', 'অর্থনীতি');
    getMappingFor('adalot', 'আদালত');
    getMappingFor('shikkha', 'শিক্ষা');
    getMappingFor('dhormo', 'ধর্ম');
    getMappingFor('projukti', 'প্রযুক্তি');
    getMappingFor('baktitwa', 'ব্যাক্তিত্ব');
    getMappingFor('feature', 'ফিচার');
    getMappingFor('kheladhula', 'খেলাধুলা');
    getMappingFor('binodon', 'বিনোদন');
    getMappingFor('vhromon', 'ভ্রমণ');
    getMappingFor('lifestyle', 'লাইফস্টাইল');
    getMappingFor('rannaghor', 'রান্নাঘর');

    // Sub-widgets
    getMappingFor('editorial', 'সম্পাদকীয়');
    getMappingFor('muktijuddho', 'মুক্তিযুদ্ধ');
    getMappingFor('crime', 'ক্রাইম');
    getMappingFor('paribesh', 'পরিবেশ');
    getMappingFor('probash', 'প্রবাস');
    getMappingFor('banijjo', 'বাণিজ্য');
    getMappingFor('krishi', 'কৃষিবার্তা');
    getMappingFor('facebook', 'ফেসবুক');
    getMappingFor('shikkangon', 'শিক্ষাঙ্গন');
    getMappingFor('chakri', 'চাকরির খবর');
    getMappingFor('sahityo', 'সাহিত্য');
    getMappingFor('porjoton', 'পর্যটন');
    getMappingFor('rupchorcha', 'রূপচর্চা');
    getMappingFor('shastho', 'স্বাস্থ্য');

    // Dynamic fallback backfilling for slots that don't have matching categories
    const remaining = available.filter(l => !used.has(l));
    let remIdx = 0;

    const keys = Object.keys(mapping);
    for (const k of keys) {
      const currentTarget = mapping[k];
      if (!available.includes(currentTarget) && remIdx < remaining.length) {
        mapping[k] = remaining[remIdx++];
        used.add(mapping[k]);
      }
    }

    return mapping;
  }, [categoriesData]);

  const [loadMore, setLoadMore] = useState(false);
  const { globals, sections: dynamicSections } = useGlobals();
  
  useEffect(() => {
    trackPageView('home');
  }, []);

  useEffect(() => {
    const handleScroll = () => { setLoadMore(true); window.removeEventListener('scroll', handleScroll); };
    window.addEventListener('scroll', handleScroll, { passive: true });
    const timer = setTimeout(() => setLoadMore(true), 1200); // fallback if no scroll
    return () => { clearTimeout(timer); window.removeEventListener('scroll', handleScroll); };
  }, []);

  const { data: sebaHotNewsData, isLoading: sebaHotNewsLoading } = useSWR('seba_hot_news', () => bloggerService.getPostsByLabel('Seba Hot News', 6));
  const sebaHotNewsPosts = sebaHotNewsData?.items || [];
  const featuredBannerPost = sebaHotNewsPosts[0];
  const leftPosts = sebaHotNewsPosts.length > 2 ? sebaHotNewsPosts.slice(1, 3) : [];
  const middleBottomPosts = sebaHotNewsPosts.length > 4 ? sebaHotNewsPosts.slice(3, 5) : [];

  const { data: sebaTopNewsData, isLoading: sebaTopNewsLoading } = useSWR('seba_top_news', () => bloggerService.getPostsByLabel('Seba Top News', 6));
  const sebaTopNewsPosts = sebaTopNewsData?.items || [];

  const { data: trendingNewsData, isLoading: trendingNewsLoading } = useSWR('trending_news', () => bloggerService.getPostsByLabel('Trending', 5));
  const trendingNewsPosts = trendingNewsData?.items || [];
  const displayTrendingPosts = trendingNewsPosts.length > 0 ? trendingNewsPosts : (postsData?.items?.slice(0, 5) || []);

  const { data: nationalNewsData, isLoading: nationalNewsLoading } = useSWR(resolvedLabels.national ? `national_news_${resolvedLabels.national}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.national, 5));
  const nationalNewsPosts = nationalNewsData?.items || [];

  const { data: bdNewsData, isLoading: bdNewsLoading } = useSWR(resolvedLabels.bd ? `bd_news_${resolvedLabels.bd}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.bd, 5));
  const bdNewsPosts = bdNewsData?.items || [];

  const { data: worldNewsData, isLoading: worldNewsLoading } = useSWR(resolvedLabels.world ? `world_news_${resolvedLabels.world}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.world, 5));
  const worldNewsPosts = worldNewsData?.items || [];

  const { data: politicsNewsData, isLoading: politicsNewsLoading } = useSWR(resolvedLabels.politics ? `politics_news_${resolvedLabels.politics}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.politics, 6));
  const politicsNewsPosts = politicsNewsData?.items || [];

  const { data: economyNewsData, isLoading: economyNewsLoading } = useSWR(resolvedLabels.economy ? `economy_news_${resolvedLabels.economy}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.economy, 5));
  const economyNewsPosts = economyNewsData?.items || [];

  const { data: adalotNewsData, isLoading: adalotNewsLoading } = useSWR(resolvedLabels.adalot ? `adalot_news_${resolvedLabels.adalot}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.adalot, 6));
  const adalotNewsPosts = adalotNewsData?.items || [];

  const { data: shikkhaNewsData, isLoading: shikkhaNewsLoading } = useSWR(resolvedLabels.shikkha ? `shikkha_news_${resolvedLabels.shikkha}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.shikkha, 6));
  const shikkhaNewsPosts = shikkhaNewsData?.items || [];

  const { data: dhormoNewsData, isLoading: dhormoNewsLoading } = useSWR(loadMore && resolvedLabels.dhormo ? `dhormo_news_${resolvedLabels.dhormo}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.dhormo, 6));
  const dhormoNewsPosts = dhormoNewsData?.items || [];

  const { data: projuktiNewsData, isLoading: projuktiNewsLoading } = useSWR(loadMore && resolvedLabels.projukti ? `projukti_news_${resolvedLabels.projukti}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.projukti, 5));
  const projuktiNewsPosts = projuktiNewsData?.items || [];

  const { data: baktitwaNewsData, isLoading: baktitwaNewsLoading } = useSWR(loadMore && resolvedLabels.baktitwa ? `baktitwa_news_${resolvedLabels.baktitwa}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.baktitwa, 6));
  const baktitwaNewsPosts = baktitwaNewsData?.items || [];

  const { data: featureNewsData, isLoading: featureNewsLoading } = useSWR(loadMore && resolvedLabels.feature ? `feature_news_${resolvedLabels.feature}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.feature, 5));
  const featureNewsPosts = featureNewsData?.items || [];

  const { data: kheladhulaNewsData, isLoading: kheladhulaNewsLoading } = useSWR(loadMore && resolvedLabels.kheladhula ? `kheladhula_news_${resolvedLabels.kheladhula}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.kheladhula, 6));
  const kheladhulaNewsPosts = kheladhulaNewsData?.items || [];

  const { data: binodonNewsData, isLoading: binodonNewsLoading } = useSWR(loadMore && resolvedLabels.binodon ? `binodon_news_${resolvedLabels.binodon}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.binodon, 6));
  const binodonNewsPosts = binodonNewsData?.items || [];

  const { data: vhromonNewsData, isLoading: vhromonNewsLoading } = useSWR(loadMore && resolvedLabels.vhromon ? `vhromon_news_${resolvedLabels.vhromon}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.vhromon, 5));
  const vhromonNewsPosts = vhromonNewsData?.items || [];

  const { data: lifestyleNewsData, isLoading: lifestyleNewsLoading } = useSWR(loadMore && resolvedLabels.lifestyle ? `lifestyle_news_${resolvedLabels.lifestyle}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.lifestyle, 6));
  const lifestyleNewsPosts = lifestyleNewsData?.items || [];

  const { data: rannaghorNewsData, isLoading: rannaghorNewsLoading } = useSWR(loadMore && resolvedLabels.rannaghor ? `rannaghor_news_${resolvedLabels.rannaghor}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.rannaghor, 5));
  const rannaghorNewsPosts = rannaghorNewsData?.items || [];

  const { data: editorialNewsData, isLoading: editorialNewsLoading } = useSWR(loadMore && resolvedLabels.editorial ? `editorial_news_${resolvedLabels.editorial}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.editorial, 5));
  const editorialNewsPosts = editorialNewsData?.items || [];

  const { data: muktijuddhoNewsData, isLoading: muktijuddhoNewsLoading } = useSWR(loadMore && resolvedLabels.muktijuddho ? `muktijuddho_news_${resolvedLabels.muktijuddho}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.muktijuddho, 5));
  const muktijuddhoNewsPosts = muktijuddhoNewsData?.items || [];

  const { data: crimeNewsData, isLoading: crimeNewsLoading } = useSWR(loadMore && resolvedLabels.crime ? `crime_news_${resolvedLabels.crime}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.crime, 5));
  const crimeNewsPosts = crimeNewsData?.items || [];

  const { data: paribeshNewsData, isLoading: paribeshNewsLoading } = useSWR(loadMore && resolvedLabels.paribesh ? `paribesh_news_${resolvedLabels.paribesh}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.paribesh, 5));
  const paribeshNewsPosts = paribeshNewsData?.items || [];

  const { data: probashNewsData, isLoading: probashNewsLoading } = useSWR(loadMore && resolvedLabels.probash ? `probash_news_${resolvedLabels.probash}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.probash, 5));
  const probashNewsPosts = probashNewsData?.items || [];

  const { data: banijjoNewsData, isLoading: banijjoNewsLoading } = useSWR(loadMore && resolvedLabels.banijjo ? `banijjo_news_${resolvedLabels.banijjo}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.banijjo, 5));
  const banijjoNewsPosts = banijjoNewsData?.items || [];

  const { data: krishiNewsData, isLoading: krishiNewsLoading } = useSWR(loadMore && resolvedLabels.krishi ? `krishi_news_${resolvedLabels.krishi}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.krishi, 5));
  const krishiNewsPosts = krishiNewsData?.items || [];

  const { data: facebookNewsData, isLoading: facebookNewsLoading } = useSWR(loadMore && resolvedLabels.facebook ? `facebook_news_${resolvedLabels.facebook}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.facebook, 5));
  const facebookNewsPosts = facebookNewsData?.items || [];

  const { data: shikkangonNewsData, isLoading: shikkangonNewsLoading } = useSWR(loadMore && resolvedLabels.shikkangon ? `shikkangon_news_${resolvedLabels.shikkangon}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.shikkangon, 5));
  const shikkangonNewsPosts = shikkangonNewsData?.items || [];

  const { data: chakriNewsData, isLoading: chakriNewsLoading } = useSWR(loadMore && resolvedLabels.chakri ? `chakri_news_${resolvedLabels.chakri}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.chakri, 5));
  const chakriNewsPosts = chakriNewsData?.items || [];

  const { data: sahityoNewsData, isLoading: sahityoNewsLoading } = useSWR(loadMore && resolvedLabels.sahityo ? `sahityo_news_${resolvedLabels.sahityo}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.sahityo, 5));
  const sahityoNewsPosts = sahityoNewsData?.items || [];

  const { data: porjotonNewsData, isLoading: porjotonNewsLoading } = useSWR(loadMore && resolvedLabels.porjoton ? `porjoton_news_${resolvedLabels.porjoton}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.porjoton, 5));
  const porjotonNewsPosts = porjotonNewsData?.items || [];

  const { data: rupchorchaNewsData, isLoading: rupchorchaNewsLoading } = useSWR(loadMore && resolvedLabels.rupchorcha ? `rupchorcha_news_${resolvedLabels.rupchorcha}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.rupchorcha, 5));
  const rupchorchaNewsPosts = rupchorchaNewsData?.items || [];

  const { data: shasthoNewsData, isLoading: shasthoNewsLoading } = useSWR(loadMore && resolvedLabels.shastho ? `shastho_news_${resolvedLabels.shastho}` : null, () => bloggerService.getPostsByLabel(resolvedLabels.shastho, 5));
  const shasthoNewsPosts = shasthoNewsData?.items || [];



  const handleRefresh = async () => {
    await Promise.all([
      mutate(fetchKey),
      mutate('seba_hot_news'),
      mutate('seba_top_news'),
      mutate('trending_news'),
      mutate('national_news'),
      mutate('bd_news'),
      mutate('world_news'),
      mutate('politics_news'),
      mutate('economy_news'),
      mutate('adalot_news'),
      mutate('shikkha_news'),
      mutate('dhormo_news'),
      mutate('projukti_news'),
      mutate('baktitwa_news'),
      mutate('feature_news'),
      mutate('kheladhula_news'),
      mutate('binodon_news'),
      mutate('vhromon_news'),
      mutate('lifestyle_news'),
      mutate('rannaghor_news'),
      mutate('editorial_news'),
      mutate('muktijuddho_news'),
      mutate('crime_news'),
      mutate('paribesh_news'),
      mutate('probash_news'),
      mutate('banijjo_news'),
      mutate('krishi_news'),
      mutate('facebook_news'),
      mutate('shikkangon_news'),
      mutate('chakri_news'),
      mutate('sahityo_news'),
      mutate('porjoton_news'),
      mutate('rupchorcha_news'),
      mutate('shastho_news'),

    ]);
  };

  const posts = postsData?.items || [];
  const latestPosts = showAllLatest ? posts : posts.slice(0, 5);

  const renderLatestNews = () => (
    <div className="w-full">
       <div className="flex items-center justify-between mb-0 border-b border-[#E5E5E5] dark:border-gray-800 pb-0">
          <h2 className="text-[16px] xl:text-[18px] font-bold font-bengali text-[#111111] dark:text-white flex items-center">
            <span className="w-1 h-3.5 xl:w-1.5 xl:h-4 bg-[#D32F2F] mr-2"></span>
            সর্বশেষ সংবাদ
          </h2>
          <Link 
            to={`/category/${encodeURIComponent('সব')}`}
            className="text-[#D32F2F] text-sm font-bold font-bengali hover:text-red-700 active:scale-95 transition-transform pb-1"
          >
            সব খবর
          </Link>
        </div>
        <div className="flex flex-col">
          {postsLoading ? (
            Array(5).fill(0).map((_, i) => (
              <div key={i} className={`flex gap-3 py-1.5 min-h-[70px] xl:min-h-[76px] items-start ${i !== 4 ? 'border-b border-gray-100 dark:border-gray-800' : ''}`}>
                <div className="flex-1">
                  <div className="h-4 bg-gray-100 dark:bg-gray-800 rounded animate-pulse mb-1 w-full"></div>
                  <div className="h-4 bg-gray-100 dark:bg-gray-800 rounded animate-pulse mb-1 w-4/5"></div>
                  <div className="h-4 bg-gray-100 dark:bg-gray-800 rounded animate-pulse w-3/5"></div>
                </div>
                <div className="w-[100px] xl:w-[110px] h-[56px] xl:h-[62px] bg-gray-100 dark:bg-gray-800 rounded animate-pulse shrink-0"></div>
              </div>
            ))
          ) : (
            <>
              {latestPosts.map((post, i) => (
                <Link key={post.id} to={post.url ? new URL(post.url).pathname : `/post/${post.id}`} className={`group flex gap-3 py-1.5 min-h-[70px] xl:min-h-[76px] items-start ${i !== latestPosts.length - 1 ? 'border-b border-gray-100 dark:border-gray-800' : ''}`}>
                  <div className="flex-1">
                    <h3 className="text-[14px] xl:text-[13px] font-semibold font-bengali text-[#111111] dark:text-gray-200 group-hover:text-[#D32F2F] dark:group-hover:text-[#D32F2F] leading-[1.4] line-clamp-3 mb-1 transition-colors">
                      {post.title}
                    </h3>
                  </div>
                  {extractFirstImage(post.content) && (
                    <div className="w-[100px] xl:w-[110px] h-[56px] xl:h-[62px] shrink-0 overflow-hidden rounded relative">
                      <img src={extractFirstImage(post.content)!} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" referrerPolicy="no-referrer" />
                    </div>
                  )}
                </Link>
              ))}
            </>
          )}
        </div>
    </div>
  );

  return (
    <Layout>
      <SEO preloadImage={featuredBannerPost ? extractFirstImage(featuredBannerPost.content, 1000, 562) : undefined} />
      <PullToRefresh onRefresh={handleRefresh}>
        
        <div className="px-4 py-[10px]">
          {activeCategory === 'সব' && <WebStoriesRow />}
          {activeCategory === 'সব' ? (
            dynamicSections.length > 0 ? (
              <div className="w-full flex flex-col gap-6 mt-0">
                {dynamicSections.map(sec => <DynamicSection key={sec.id} section={sec} />)}
              </div>
            ) : (
            <>
              {sebaHotNewsLoading && sebaHotNewsPosts.length === 0 ? (
                <div className="mb-0 py-[10px] w-full"><HomeGridSkeleton /></div>
              ) : sebaHotNewsPosts.length > 0 ? (
              <>
                {/* Main Desktop Grid */}
                <div className="mb-0 py-[10px] hidden md:block">
                  <div className="flex flex-col md:grid md:grid-cols-[minmax(0,1fr)_260px] lg:grid-cols-[250px_minmax(0,1fr)_280px] xl:grid-cols-[280px_minmax(0,1fr)_320px] gap-6 lg:gap-3 xl:gap-4 items-start">
                    
                    {/* LEFT COL: Secondary News List */}
                    <div className="order-2 lg:order-1 flex flex-col gap-6 w-full md:col-span-1 lg:border-r border-[#E5E5E5] dark:border-gray-800 lg:pr-3 xl:pr-4">
                       {leftPosts.map((post, i) => (
                         <div key={post.id} className={cn(i !== leftPosts.length - 1 && "border-b border-[#E5E5E5] dark:border-gray-800 pb-6")}>
                           <CompactPost post={post} imageVariant="small" />
                         </div>
                       ))}
                    </div>

                    {/* MIDDLE COL: Main Featured */}
                    <div className="order-1 lg:order-2 w-full md:col-span-1 border-b md:border-b-0 md:border-r border-[#E5E5E5] dark:border-gray-800 pb-6 md:pb-0 md:pr-3 lg:pr-3 xl:pr-4">
                      {featuredBannerPost && (
                        <Link to={featuredBannerPost.url ? new URL(featuredBannerPost.url).pathname : `/post/${featuredBannerPost.id}`} className="group flex flex-col md:flex-col lg:flex-row gap-4 lg:gap-4 mb-6">
                          <div className="lg:w-[50%] shrink-0 overflow-hidden relative bg-gray-100 dark:bg-gray-800 order-1 lg:order-1">
                             <img src={extractFirstImage(featuredBannerPost.content, 1000, 562) || ''} alt="" className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-500" loading="eager" fetchPriority="high" decoding="sync" />
                          </div>
                          <div className="lg:w-[50%] flex flex-col pt-1 order-2 lg:order-2">
                             <h2 className="text-[18px] xl:text-[21px] font-bold leading-tight font-bengali text-[#111111] dark:text-white group-hover:text-[#D32F2F] transition-colors mb-2">
                               {featuredBannerPost.title}
                             </h2>
                             <div className="text-[#444444] dark:text-gray-300 text-[14px] xl:text-[15px] line-clamp-3 font-bengali leading-relaxed mb-3">
                               {stripHtml(featuredBannerPost.content).substring(0, 150)}...
                             </div>
                             <div className="text-[12px] text-[#666666] mt-auto font-bengali">
                                {formatBDDate(featuredBannerPost.published, true)}
                             </div>
                          </div>
                        </Link>
                      )}
                      
                      <div className="border-t border-[#E5E5E5] dark:border-gray-800 my-6"></div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-3 xl:gap-4">
                        {middleBottomPosts.map((post, i) => (
                          <div key={post.id} className={cn(i === 0 && "md:border-r border-[#E5E5E5] dark:border-gray-800 md:pr-3 xl:pr-4")}>
                            <CompactPost post={post} imageVariant="square" />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* RIGHT COL: Latest News (সর্বশেষ সংবাদ) */}
                    <div className="order-3 w-full md:col-span-1 md:row-span-2 lg:row-span-1 lg:sticky lg:top-[120px]">
                       {renderLatestNews()}
                    </div>
                  </div>
              </div>

              {/* Mobile Seba Hot News (Visible only on mobile/tablet) */}
              <div className="mb-0 py-[10px] md:hidden">
                  <div className="flex flex-col gap-4">
                    {featuredBannerPost && (
                      <NewsCard post={featuredBannerPost} featured={true} priority={true} />
                    )}
                    {sebaHotNewsPosts.length > 1 && (
                      <div className="-mx-4 px-4 overflow-hidden relative">
                        <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-2">
                           {sebaHotNewsPosts.slice(1, 6).map(post => (
                             <div key={post.id} className="w-[180px] md:w-[220px] snap-center shrink-0">
                               <NewsCard post={post} variant="slider" />
                             </div>
                           ))}
                        </div>
                      </div>
                    )}
                  </div>
              </div>

              {/* Mobile Latest News */}
              <div className="mb-0 py-[10px] md:hidden px-0">
                 {renderLatestNews()}
              </div>
                  
              {/* Seba Top News */}
              {!sebaHotNewsLoading && sebaTopNewsPosts.length > 0 && (
                <div className="mb-0 py-[10px] w-full">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 md:gap-4">
                     {sebaTopNewsPosts.slice(0, 6).map(post => (
                       <NewsCard key={post.id} post={post} variant="grid" />
                     ))}
                  </div>
                </div>
              )}
            </>
            ) : null}
            </>
            )
          ) : (

          <div className="flex flex-col xl:flex-row gap-6 xl:gap-8 mb-8">
            <div className="w-full xl:w-[800px] shrink-0">
              {postsLoading && !postsData ? (
                <CategoryGridSkeleton />
              ) : posts.length > 0 ? (
                 <>
                   <div className="mb-6">
                     <NewsCard post={posts[0]} featured={true} />
                   </div>
                   <div className="grid grid-cols-2 gap-4 xl:gap-6">
                     {posts.slice(1, 7).map(post => (
                       <NewsCard key={post.id} post={post} variant="grid" />
                     ))}
                   </div>
                 </>
              ) : (
                <div className="p-8 text-center bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-gray-500 dark:text-gray-400 font-bengali">কোন খবর পাওয়া যায়নি</p>
                </div>
              )}
            </div>

            <div className="hidden xl:flex w-[300px] shrink-0 flex-col gap-8">
              {renderLatestNews()}
              <div className="sticky top-[140px]">
                <div className="bg-gray-100 dark:bg-gray-800 rounded-lg w-full min-h-[400px] flex items-center justify-center border border-gray-200 dark:border-gray-700 overflow-hidden p-2">
                  <Suspense fallback={<div className="animate-pulse bg-gray-200 dark:bg-gray-700 w-[300px] h-[250px] rounded-lg"></div>}>
                    <AdBanner />
                  </Suspense>
                </div>
              </div>
            </div>
            
            <div className="xl:hidden w-full mt-6 mb-8">
               {renderLatestNews()}
            </div>
          </div>
        )}

          {/* Trending News */}
          {activeCategory === 'সব' && dynamicSections.length === 0 && (!trendingNewsLoading || displayTrendingPosts.length > 0) && displayTrendingPosts.length > 0 && (
            <div className="mb-0 py-[10px] w-full">
              <div className="flex items-center justify-between mb-0 pr-1.5 w-full">
                 <h2 className="text-lg font-bold font-bengali text-[#111111] dark:text-white flex items-center">
                   <span className="w-1 h-5 bg-[#D32F2F] rounded-full mr-2"></span>
                   ট্রেন্ডিং নিউজ
                 </h2>
              </div>
              <div className="-mx-4 px-4 overflow-hidden relative">
                <div className="flex overflow-x-auto gap-3 md:gap-4 pb-4 snap-x snap-mandatory scrollbar-hide">
                  {displayTrendingPosts.map(post => (
                    <div key={post.id} className="w-[140px] md:w-[180px] xl:w-[200px] shrink-0 snap-start">
                      <NewsCard post={post} variant="grid" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* জাতীয় News */}
          {activeCategory === 'সব' && dynamicSections.length === 0 && !nationalNewsLoading && nationalNewsPosts.length > 0 && (
            <div className='mb-0 py-[10px] flex flex-col xl:flex-row gap-6 w-full'>
              <div className='w-full xl:w-[760px] shrink-0'>
                <div className='flex items-center justify-between mb-0 pr-1.5 w-full'>
                 <h2 className='text-lg font-bold font-bengali text-[#111111] dark:text-white flex items-center'>
                   <span className='w-1 h-5 bg-[#D32F2F] rounded-full mr-2'></span>{resolvedLabels.national || 'জাতীয়'}
                 </h2>
                 <Link to={`/category/${encodeURIComponent(resolvedLabels.national || 'জাতীয়')}`} className='text-[#D32F2F] text-sm font-bold font-bengali hover:text-red-700 active:scale-95 transition-transform' aria-label='জাতীয় এর আরও খবর'>আরও</Link>
                </div>
                 <div className='flex flex-col md:grid md:grid-cols-2 gap-6 w-full'>
                    {nationalNewsPosts[0] && (
                      <div className='mb-0 w-full h-[326px]'>
                        <NewsCard post={nationalNewsPosts[0]} featured={true} />
                      </div>
                    )}
                    <div className='flex flex-col w-full h-[326px] justify-between'>
                      {nationalNewsPosts.slice(1).map((post, i) => (
                        <NewsCard key={post.id} post={post} className={i === nationalNewsPosts.length - 2 ? 'pb-0' : ''} />
                      ))}
                    </div>
                  </div>
              </div>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4 flex-1 h-full'>
                <Suspense fallback={<div className="h-[300px] w-full bg-gray-100 dark:bg-gray-800 animate-pulse rounded-lg"></div>}>
                  <WidgetPostList title={resolvedLabels.editorial || 'সম্পাদকীয়'} posts={editorialNewsPosts} loading={editorialNewsLoading} />
                </Suspense>
                <Suspense fallback={<div className="h-[300px] w-full bg-gray-100 dark:bg-gray-800 animate-pulse rounded-lg"></div>}>
                  <WidgetPostList title={resolvedLabels.muktijuddho || 'মুক্তিযুদ্ধ'} posts={muktijuddhoNewsPosts} loading={muktijuddhoNewsLoading} />
                </Suspense>
              </div>
            </div>
          )}

          {/* বাংলাদেশ News */}
          {activeCategory === 'সব' && dynamicSections.length === 0 && !bdNewsLoading && bdNewsPosts.length > 0 && (
            <div className='mb-0 py-[10px] flex flex-col xl:flex-row gap-6 w-full'>
              <div className='w-full xl:w-[760px] shrink-0'>
                <div className='flex items-center justify-between mb-0 pr-1.5 w-full'>
                 <h2 className='text-lg font-bold font-bengali text-[#111111] dark:text-white flex items-center'>
                   <span className='w-1 h-5 bg-[#D32F2F] rounded-full mr-2'></span>{resolvedLabels.bd || 'বাংলাদেশ'}
                 </h2>
                 <Link to={`/category/${encodeURIComponent(resolvedLabels.bd || 'বাংলাদেশ')}`} className='text-[#D32F2F] text-sm font-bold font-bengali hover:text-red-700 active:scale-95 transition-transform' aria-label='বাংলাদেশ এর আরও খবর'>আরও</Link>
                </div>
                 <div className='flex flex-col md:grid md:grid-cols-2 gap-6 w-full'>
                    {bdNewsPosts[0] && (
                      <div className='mb-0 w-full h-[326px]'>
                        <NewsCard post={bdNewsPosts[0]} featured={true} />
                      </div>
                    )}
                    <div className='flex flex-col w-full h-[326px] justify-between'>
                      {bdNewsPosts.slice(1).map((post, i) => (
                        <NewsCard key={post.id} post={post} className={i === bdNewsPosts.length - 2 ? 'pb-0' : ''} />
                      ))}
                    </div>
                  </div>
              </div>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4 flex-1 h-full'>
                <Suspense fallback={<div className="h-[300px] bg-gray-100 dark:bg-gray-800 animate-pulse rounded-lg"></div>}>
                  <WidgetPostList title={resolvedLabels.crime || 'ক্রাইম'} posts={crimeNewsPosts} loading={crimeNewsLoading} />
                </Suspense>
                <Suspense fallback={<div className="h-[300px] bg-gray-100 dark:bg-gray-800 animate-pulse rounded-lg"></div>}>
                  <WidgetPostList title={resolvedLabels.paribesh || 'পরিবেশ'} posts={paribeshNewsPosts} loading={paribeshNewsLoading} />
                </Suspense>
              </div>
            </div>
          )}

          {/* বিশ্ব News */}
          {activeCategory === 'সব' && dynamicSections.length === 0 && !worldNewsLoading && worldNewsPosts.length > 0 && (
            <div className='mb-0 py-[10px] flex flex-col xl:flex-row gap-6 w-full'>
              <div className='w-full xl:w-[760px] shrink-0'>
                <div className='flex items-center justify-between mb-0 pr-1.5 w-full'>
                 <h2 className='text-lg font-bold font-bengali text-[#111111] dark:text-white flex items-center'>
                   <span className='w-1 h-5 bg-[#D32F2F] rounded-full mr-2'></span>{resolvedLabels.world || 'বিশ্ব'}
                 </h2>
                 <Link to={`/category/${encodeURIComponent(resolvedLabels.world || 'বিশ্ব')}`} className='text-[#D32F2F] text-sm font-bold font-bengali hover:text-red-700 active:scale-95 transition-transform' aria-label='বিশ্ব এর আরও খবর'>আরও</Link>
                </div>
                 <div className='flex flex-col md:grid md:grid-cols-2 gap-6 w-full'>
                    {worldNewsPosts[0] && (
                      <div className='mb-0 w-full h-[326px]'>
                        <NewsCard post={worldNewsPosts[0]} featured={true} />
                      </div>
                    )}
                    <div className='flex flex-col w-full h-[326px] justify-between'>
                      {worldNewsPosts.slice(1).map((post, i) => (
                        <NewsCard key={post.id} post={post} className={i === worldNewsPosts.length - 2 ? 'pb-0' : ''} />
                      ))}
                    </div>
                  </div>
              </div>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4 flex-1 h-full'>
                <Suspense fallback={<div className="h-[300px] bg-gray-100 dark:bg-gray-800 animate-pulse rounded-lg"></div>}>
                  <WidgetPostList title={resolvedLabels.probash || 'প্রবাস'} posts={probashNewsPosts} loading={probashNewsLoading} />
                </Suspense>
                <AdSenseWidget />
              </div>
            </div>
          )}

          {/* রাজনীতি News */}
          {activeCategory === 'সব' && dynamicSections.length === 0 && !politicsNewsLoading && politicsNewsPosts.length > 0 && (
            <div className='mb-0 py-[10px] w-full'>
              <div className='flex items-center justify-between mb-0 pr-1.5 w-full'>
                <h2 className='text-lg font-bold font-bengali text-[#111111] dark:text-white flex items-center'>
                  <span className='w-1 h-5 bg-[#D32F2F] rounded-full mr-2'></span>{resolvedLabels.politics || 'রাজনীতি'}
                </h2>
                <Link to={`/category/${encodeURIComponent(resolvedLabels.politics || 'রাজনীতি')}`} className='text-[#D32F2F] text-sm font-bold font-bengali hover:text-red-700 active:scale-95 transition-transform' aria-label='রাজনীতি এর আরও খবর'>আরও</Link>
              </div>
              <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4'>
                 {politicsNewsPosts.slice(0, 6).map(post => (
                   <NewsCard key={post.id} post={post} variant='grid' />
                 ))}
              </div>
            </div>
          )}

          {/* অর্থনীতি News */}
          {activeCategory === 'সব' && dynamicSections.length === 0 && !economyNewsLoading && economyNewsPosts.length > 0 && (
            <div className='mb-0 py-[10px] flex flex-col xl:flex-row gap-6 w-full'>
              <div className='w-full xl:w-[760px] shrink-0'>
                <div className='flex items-center justify-between mb-0 pr-1.5 w-full'>
                 <h2 className='text-lg font-bold font-bengali text-[#111111] dark:text-white flex items-center'>
                   <span className='w-1 h-5 bg-[#D32F2F] rounded-full mr-2'></span>{resolvedLabels.economy || 'অর্থনীতি'}
                 </h2>
                 <Link to={`/category/${encodeURIComponent(resolvedLabels.economy || 'অর্থনীতি')}`} className='text-[#D32F2F] text-sm font-bold font-bengali hover:text-red-700 active:scale-95 transition-transform' aria-label='অর্থনীতি এর আরও খবর'>আরও</Link>
                </div>
                 <div className='flex flex-col md:grid md:grid-cols-2 gap-6 w-full'>
                    {economyNewsPosts[0] && (
                      <div className='mb-0 w-full h-[326px]'>
                        <NewsCard post={economyNewsPosts[0]} featured={true} />
                      </div>
                    )}
                    <div className='flex flex-col w-full h-[326px] justify-between'>
                      {economyNewsPosts.slice(1).map((post, i) => (
                        <NewsCard key={post.id} post={post} className={i === economyNewsPosts.length - 2 ? 'pb-0' : ''} />
                      ))}
                    </div>
                  </div>
              </div>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4 flex-1 h-full'>
                <Suspense fallback={<div className="h-[300px] bg-gray-100 dark:bg-gray-800 animate-pulse rounded-lg"></div>}>
                  <WidgetPostList title={resolvedLabels.banijjo || 'বাণিজ্য'} posts={banijjoNewsPosts} loading={banijjoNewsLoading} />
                </Suspense>
                <Suspense fallback={<div className="h-[300px] bg-gray-100 dark:bg-gray-800 animate-pulse rounded-lg"></div>}>
                  <WidgetPostList title={resolvedLabels.krishi || 'কৃষিবার্তা'} posts={krishiNewsPosts} loading={krishiNewsLoading} />
                </Suspense>
              </div>
            </div>
          )}

          {/* আদালত News */}
          {activeCategory === 'সব' && dynamicSections.length === 0 && !adalotNewsLoading && adalotNewsPosts.length > 0 && (
            <div className='mb-0 py-[10px] w-full'>
              <div className='flex items-center justify-between mb-0 pr-1.5 w-full'>
                <h2 className='text-lg font-bold font-bengali text-[#111111] dark:text-white flex items-center'>
                  <span className='w-1 h-5 bg-[#D32F2F] rounded-full mr-2'></span>{resolvedLabels.adalot || 'আদালত'}
                </h2>
                <Link to={`/category/${encodeURIComponent(resolvedLabels.adalot || 'আদালত')}`} className='text-[#D32F2F] text-sm font-bold font-bengali hover:text-red-700 active:scale-95 transition-transform' aria-label='আদালত এর আরও খবর'>আরও</Link>
              </div>
              <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 md:gap-4'>
                 {adalotNewsPosts.slice(0, 6).map(post => (
                   <NewsCard key={post.id} post={post} variant='grid' />
                 ))}
              </div>
            </div>
          )}

          {/* শিক্ষা News */}
          {activeCategory === 'সব' && dynamicSections.length === 0 && !shikkhaNewsLoading && shikkhaNewsPosts.length > 0 && (
            <div className='mb-0 py-[10px] w-full'>
              <div className='flex items-center justify-between mb-0 pr-1.5 w-full'>
                <h2 className='text-lg font-bold font-bengali text-[#111111] dark:text-white flex items-center'>
                  <span className='w-1 h-5 bg-[#D32F2F] rounded-full mr-2'></span>{resolvedLabels.shikkha || 'শিক্ষা'}
                </h2>
                <Link to={`/category/${encodeURIComponent(resolvedLabels.shikkha || 'শিক্ষা')}`} className='text-[#D32F2F] text-sm font-bold font-bengali hover:text-red-700 active:scale-95 transition-transform' aria-label='শিক্ষা এর আরও খবর'>আরও</Link>
              </div>
              <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 md:gap-4'>
                 {shikkhaNewsPosts.slice(0, 6).map(post => (
                   <NewsCard key={post.id} post={post} variant='grid' />
                 ))}
              </div>
            </div>
          )}

          {/* ধর্ম News */}
          {activeCategory === 'সব' && dynamicSections.length === 0 && !dhormoNewsLoading && dhormoNewsPosts.length > 0 && (
            <div className='mb-0 py-[10px] w-full'>
              <div className='flex items-center justify-between mb-0 pr-1.5 w-full'>
                <h2 className='text-lg font-bold font-bengali text-[#111111] dark:text-white flex items-center'>
                  <span className='w-1 h-5 bg-[#D32F2F] rounded-full mr-2'></span>{resolvedLabels.dhormo || 'ধর্ম'}
                </h2>
                <Link to={`/category/${encodeURIComponent(resolvedLabels.dhormo || 'ধর্ম')}`} className='text-[#D32F2F] text-sm font-bold font-bengali hover:text-red-700 active:scale-95 transition-transform' aria-label='ধর্ম এর আরও খবর'>আরও</Link>
              </div>
              <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 md:gap-4'>
                 {dhormoNewsPosts.slice(0, 6).map(post => (
                   <NewsCard key={post.id} post={post} variant='grid' />
                 ))}
              </div>
            </div>
          )}

          {/*  News */}
          {activeCategory === 'সব' && dynamicSections.length === 0 && !projuktiNewsLoading && projuktiNewsPosts.length > 0 && (
            <div className='mb-0 py-[10px] flex flex-col xl:flex-row gap-6 w-full'>
              <div className='w-full xl:w-[760px] shrink-0'>
                <div className='flex items-center justify-between mb-0 pr-1.5 w-full'>
                 <h2 className='text-lg font-bold font-bengali text-[#111111] dark:text-white flex items-center'>
                   <span className='w-1 h-5 bg-[#D32F2F] rounded-full mr-2'></span>{resolvedLabels.projukti || 'প্রযুক্তি'}
                 </h2>
                 <Link to={`/category/${encodeURIComponent(resolvedLabels.projukti || 'প্রযুক্তি')}`} className='text-[#D32F2F] text-sm font-bold font-bengali hover:text-red-700 active:scale-95 transition-transform' aria-label='প্রযুক্তি এর আরও খবর'>আরও</Link>
                </div>
                 <div className='flex flex-col md:grid md:grid-cols-2 gap-6 w-full'>
                    {projuktiNewsPosts[0] && (
                      <div className='mb-0 w-full h-[326px]'>
                        <NewsCard post={projuktiNewsPosts[0]} featured={true} />
                      </div>
                    )}
                    <div className='flex flex-col w-full h-[326px] justify-between'>
                      {projuktiNewsPosts.slice(1).map((post, i) => (
                        <NewsCard key={post.id} post={post} className={i === projuktiNewsPosts.length - 2 ? 'pb-0' : ''} />
                      ))}
                    </div>
                  </div>
              </div>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4 flex-1 h-full'>
                <Suspense fallback={<div className="h-[300px] bg-gray-100 dark:bg-gray-800 animate-pulse rounded-lg"></div>}>
                  <WidgetPostList title={resolvedLabels.facebook || 'ফেসবুক'} posts={facebookNewsPosts} loading={facebookNewsLoading} />
                </Suspense>
                <Suspense fallback={<div className="h-[300px] bg-gray-100 dark:bg-gray-800 animate-pulse rounded-lg"></div>}>
                  <WidgetPostList title={resolvedLabels.shikkangon || 'শিক্ষাঙ্গন'} posts={shikkangonNewsPosts} loading={shikkangonNewsLoading} />
                </Suspense>
              </div>
            </div>
          )}

          {/* ব্যাক্তিত্ব News */}
          {activeCategory === 'সব' && dynamicSections.length === 0 && !baktitwaNewsLoading && baktitwaNewsPosts.length > 0 && (
            <div className='mb-0 py-[10px] w-full'>
              <div className='flex items-center justify-between mb-0 pr-1.5 w-full'>
                <h2 className='text-lg font-bold font-bengali text-[#111111] dark:text-white flex items-center'>
                  <span className='w-1 h-5 bg-[#D32F2F] rounded-full mr-2'></span>{resolvedLabels.baktitwa || 'ব্যাক্তিত্ব'}
                </h2>
                <Link to={`/category/${encodeURIComponent(resolvedLabels.baktitwa || 'ব্যাক্তিত্ব')}`} className='text-[#D32F2F] text-sm font-bold font-bengali hover:text-red-700 active:scale-95 transition-transform' aria-label='ব্যাক্তিত্ব এর আরও খবর'>আরও</Link>
              </div>
              <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 md:gap-4'>
                 {baktitwaNewsPosts.slice(0, 6).map(post => (
                   <NewsCard key={post.id} post={post} variant='grid' />
                 ))}
              </div>
            </div>
          )}

          {/* ফিচার News */}
          {activeCategory === 'সব' && dynamicSections.length === 0 && !featureNewsLoading && featureNewsPosts.length > 0 && (
            <div className='mb-0 py-[10px] flex flex-col xl:flex-row gap-6 w-full'>
              <div className='w-full xl:w-[760px] shrink-0'>
                <div className='flex items-center justify-between mb-0 pr-1.5 w-full'>
                 <h2 className='text-lg font-bold font-bengali text-[#111111] dark:text-white flex items-center'>
                   <span className='w-1 h-5 bg-[#D32F2F] rounded-full mr-2'></span>{resolvedLabels.feature || 'ফিচার'}
                 </h2>
                 <Link to={`/category/${encodeURIComponent(resolvedLabels.feature || 'ফিচার')}`} className='text-[#D32F2F] text-sm font-bold font-bengali hover:text-red-700 active:scale-95 transition-transform' aria-label='ফিচার এর আরও খবর'>আরও</Link>
                </div>
                 <div className='flex flex-col md:grid md:grid-cols-2 gap-6 w-full'>
                    {featureNewsPosts[0] && (
                      <div className='mb-0 w-full h-[326px]'>
                        <NewsCard post={featureNewsPosts[0]} featured={true} />
                      </div>
                    )}
                    <div className='flex flex-col w-full h-[326px] justify-between'>
                      {featureNewsPosts.slice(1).map((post, i) => (
                        <NewsCard key={post.id} post={post} className={i === featureNewsPosts.length - 2 ? 'pb-0' : ''} />
                      ))}
                    </div>
                  </div>
              </div>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4 flex-1 h-full'>
                <Suspense fallback={<div className="h-[300px] bg-gray-100 dark:bg-gray-800 animate-pulse rounded-lg"></div>}>
                  <WidgetPostList title={resolvedLabels.chakri || 'চাকরির খবর'} posts={chakriNewsPosts} loading={chakriNewsLoading} />
                </Suspense>
                <Suspense fallback={<div className="h-[300px] bg-gray-100 dark:bg-gray-800 animate-pulse rounded-lg"></div>}>
                  <WidgetPostList title={resolvedLabels.sahityo || 'সাহিত্য'} posts={sahityoNewsPosts} loading={sahityoNewsLoading} />
                </Suspense>
              </div>
            </div>
          )}

          {/* খেলাধুলা News */}
          {activeCategory === 'সব' && dynamicSections.length === 0 && !kheladhulaNewsLoading && kheladhulaNewsPosts.length > 0 && (
            <div className='mb-0 py-[10px] w-full'>
              <div className='flex items-center justify-between mb-0 pr-1.5 w-full'>
                <h2 className='text-lg font-bold font-bengali text-[#111111] dark:text-white flex items-center'>
                  <span className='w-1 h-5 bg-[#D32F2F] rounded-full mr-2'></span>{resolvedLabels.kheladhula || 'খেলাধুলা'}
                </h2>
                <Link to={`/category/${encodeURIComponent(resolvedLabels.kheladhula || 'খেলাধুলা')}`} className='text-[#D32F2F] text-sm font-bold font-bengali hover:text-red-700 active:scale-95 transition-transform' aria-label='খেলাধুলা এর আরও খবর'>আরও</Link>
              </div>
              <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 md:gap-4'>
                 {kheladhulaNewsPosts.slice(0, 6).map(post => (
                   <NewsCard key={post.id} post={post} variant='grid' />
                 ))}
              </div>
            </div>
          )}

          {/* বিনোদন News */}
          {activeCategory === 'সব' && dynamicSections.length === 0 && !binodonNewsLoading && binodonNewsPosts.length > 0 && (
            <div className='mb-0 py-[10px] w-full'>
              <div className='flex items-center justify-between mb-0 pr-1.5 w-full'>
                <h2 className='text-lg font-bold font-bengali text-[#111111] dark:text-white flex items-center'>
                  <span className='w-1 h-5 bg-[#D32F2F] rounded-full mr-2'></span>{resolvedLabels.binodon || 'বিনোদন'}
                </h2>
                <Link to={`/category/${encodeURIComponent(resolvedLabels.binodon || 'বিনোদন')}`} className='text-[#D32F2F] text-sm font-bold font-bengali hover:text-red-700 active:scale-95 transition-transform' aria-label='বিনোদন এর আরও খবর'>আরও</Link>
              </div>
              <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 md:gap-4'>
                 {binodonNewsPosts.slice(0, 6).map(post => (
                   <NewsCard key={post.id} post={post} variant='grid' />
                 ))}
              </div>
            </div>
          )}

          {/* ভ্রমণ News */}
          {activeCategory === 'সব' && dynamicSections.length === 0 && !vhromonNewsLoading && vhromonNewsPosts.length > 0 && (
            <div className='mb-0 py-[10px] flex flex-col xl:flex-row gap-6 w-full'>
              <div className='w-full xl:w-[760px] shrink-0'>
                <div className='flex items-center justify-between mb-0 pr-1.5 w-full'>
                 <h2 className='text-lg font-bold font-bengali text-[#111111] dark:text-white flex items-center'>
                   <span className='w-1 h-5 bg-[#D32F2F] rounded-full mr-2'></span>{resolvedLabels.vhromon || 'ভ্রমণ'}
                 </h2>
                 <Link to={`/category/${encodeURIComponent(resolvedLabels.vhromon || 'ভ্রমণ')}`} className='text-[#D32F2F] text-sm font-bold font-bengali hover:text-red-700 active:scale-95 transition-transform' aria-label='ভ্রমণ এর আরও খবর'>আরও</Link>
                </div>
                 <div className='flex flex-col md:grid md:grid-cols-2 gap-6 w-full'>
                    {vhromonNewsPosts[0] && (
                      <div className='mb-0 w-full h-[326px]'>
                        <NewsCard post={vhromonNewsPosts[0]} featured={true} />
                      </div>
                    )}
                    <div className='flex flex-col w-full h-[326px] justify-between'>
                      {vhromonNewsPosts.slice(1).map((post, i) => (
                        <NewsCard key={post.id} post={post} className={i === vhromonNewsPosts.length - 2 ? 'pb-0' : ''} />
                      ))}
                    </div>
                  </div>
              </div>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4 flex-1 h-full'>
                <Suspense fallback={<div className="h-[300px] bg-gray-100 dark:bg-gray-800 animate-pulse rounded-lg"></div>}>
                  <WidgetPostList title={resolvedLabels.porjoton || 'পর্যটন'} posts={porjotonNewsPosts} loading={porjotonNewsLoading} />
                </Suspense>
                <AdSenseWidget />
              </div>
            </div>
          )}

          {/* লাইফস্টাইল News */}
          {activeCategory === 'সব' && dynamicSections.length === 0 && !lifestyleNewsLoading && lifestyleNewsPosts.length > 0 && (
            <div className='mb-0 py-[10px] w-full'>
              <div className='flex items-center justify-between mb-0 pr-1.5 w-full'>
                <h2 className='text-lg font-bold font-bengali text-[#111111] dark:text-white flex items-center'>
                  <span className='w-1 h-5 bg-[#D32F2F] rounded-full mr-2'></span>{resolvedLabels.lifestyle || 'লাইফস্টাইল'}
                </h2>
                <Link to={`/category/${encodeURIComponent(resolvedLabels.lifestyle || 'লাইফস্টাইল')}`} className='text-[#D32F2F] text-sm font-bold font-bengali hover:text-red-700 active:scale-95 transition-transform' aria-label='লাইফস্টাইল এর আরও খবর'>আরও</Link>
              </div>
              <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 md:gap-4'>
                 {lifestyleNewsPosts.slice(0, 6).map(post => (
                   <NewsCard key={post.id} post={post} variant='grid' />
                 ))}
              </div>
            </div>
          )}

          {/* রান্নাঘর News */}
          {activeCategory === 'সব' && dynamicSections.length === 0 && !rannaghorNewsLoading && rannaghorNewsPosts.length > 0 && (
            <div className='mb-0 py-[10px] flex flex-col xl:flex-row gap-6 w-full'>
              <div className='w-full xl:w-[760px] shrink-0'>
                <div className='flex items-center justify-between mb-0 pr-1.5 w-full'>
                 <h2 className='text-lg font-bold font-bengali text-[#111111] dark:text-white flex items-center'>
                   <span className='w-1 h-5 bg-[#D32F2F] rounded-full mr-2'></span>{resolvedLabels.rannaghor || 'রান্নাঘর'}
                 </h2>
                 <Link to={`/category/${encodeURIComponent(resolvedLabels.rannaghor || 'রান্নাঘর')}`} className='text-[#D32F2F] text-sm font-bold font-bengali hover:text-red-700 active:scale-95 transition-transform' aria-label='রান্নাঘর এর আরও খবর'>আরও</Link>
                </div>
                 <div className='flex flex-col md:grid md:grid-cols-2 gap-6 w-full'>
                    {rannaghorNewsPosts[0] && (
                      <div className='mb-0 w-full h-[326px]'>
                        <NewsCard post={rannaghorNewsPosts[0]} featured={true} />
                      </div>
                    )}
                    <div className='flex flex-col w-full h-[326px] justify-between'>
                      {rannaghorNewsPosts.slice(1).map((post, i) => (
                        <NewsCard key={post.id} post={post} className={i === rannaghorNewsPosts.length - 2 ? 'pb-0' : ''} />
                      ))}
                    </div>
                  </div>
              </div>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4 flex-1 h-full'>
                <Suspense fallback={<div className="h-[300px] bg-gray-100 dark:bg-gray-800 animate-pulse rounded-lg"></div>}>
                  <WidgetPostList title={resolvedLabels.rupchorcha || 'রূপচর্চা'} posts={rupchorchaNewsPosts} loading={rupchorchaNewsLoading} />
                </Suspense>
                <Suspense fallback={<div className="h-[300px] bg-gray-100 dark:bg-gray-800 animate-pulse rounded-lg"></div>}>
                  <WidgetPostList title={resolvedLabels.shastho || 'স্বাস্থ্য'} posts={shasthoNewsPosts} loading={shasthoNewsLoading} />
                </Suspense>
              </div>
            </div>
          )}

          {(activeCategory === 'all' || activeCategory === 'সব') && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6 min-h-[400px]">
              <Suspense fallback={<div className="h-[400px] w-full bg-gray-100 dark:bg-gray-800 animate-pulse rounded-lg"></div>}>
                 <YoutubeGallery />
              </Suspense>
              <Suspense fallback={<div className="h-[400px] w-full bg-gray-100 dark:bg-gray-800 animate-pulse rounded-lg"></div>}>
                 <PhotoGallery />
              </Suspense>
            </div>
          )}
        </div>
      </PullToRefresh>
      <StickyAdminBreakingNews />
    </Layout>
  );
}